/**
 * @lodex-pro/web-admin/features/dashboard/hooks — Task 25.8 (DEC-119).
 *
 * One aggregate read (/v1/platform/dashboard) that composes the metrics the console
 * landing page needs, so the dashboard loads with a single request.
 */
import { useApiQuery, useApiOpts } from '@lodex-pro/web-shared';

export interface DashboardData {
  readonly tenants: {
    readonly total: number;
    readonly active: number;
    readonly suspended: number;
    readonly missingAbbreviation: number;
    readonly setupComplete: number;
  };
  readonly catalog: {
    readonly wells: { total: number; active: number };
    readonly operators: { total: number; active: number };
    readonly disposalWells: number;
    readonly tankCatalog: number;
  };
  readonly sync: {
    readonly lastSyncAt: string | null;
    readonly lastSyncStatus: string | null;
    readonly lastSyncError: string | null;
    readonly nextSyncAt: string | null;
    readonly pendingChanges: number;
  };
  readonly syncAgencies: ReadonlyArray<{
    readonly stateAgency: string;
    readonly isActive: boolean;
    readonly lastSyncAt: string | null;
    readonly lastSyncStatus: string | null;
    readonly nextSyncAt: string | null;
  }>;
  readonly abbreviations: {
    readonly haulersTotal: number;
    readonly haulersReady: number;
    readonly haulersMissing: number;
    readonly operatorsMissing: number;
    readonly haulIdReady: boolean;
    readonly haulersMissingList: ReadonlyArray<{ id: string; name: string }>;
  };
  readonly recentActivity: ReadonlyArray<{
    readonly id: string;
    readonly stateAgency: string;
    readonly status: string | null;
    readonly recordsAdded: number | null;
    readonly startedAt: string;
    readonly completedAt: string | null;
    readonly errorMessage: string | null;
  }>;
}

export const DASHBOARD_KEY = ['platform', 'dashboard'] as const;

export function useDashboard(): ReturnType<typeof useApiQuery<DashboardData>> {
  const opts = useApiOpts();
  return useApiQuery<DashboardData>(opts, '/v1/platform/dashboard', {
    queryKey: DASHBOARD_KEY,
    staleTime: 15_000,
  });
}
